import { AssetInfo, AuthenticatedUser, Tokens } from "~/entities/authEntities";
import Client from "../baseClient";
import { getSubdomainFromRequest } from "~/helpers/requestHelpers";
import { getClientIPAddress } from "remix-utils/get-client-ip-address";

interface PasswordlessPayload {
  email?: string;
  type: "email" | "sms";
  phone?: string;
  forceEmail?: boolean;
}

interface PasswordlessResponse {
  comoRequireSignup: boolean;
}

interface VerifyPayload {
  code: string;
  email?: string;
  phone?: string;
  orderId?: string;
  siteId?: string;
}

interface VerifyTempPayload {
  tempToken: string;
  orderId?: string;
  siteId?: string;
}

interface VerifyResponse {
  tokens: Tokens;
  authenticatedUser?: AuthenticatedUser;
  verifiedEmail?: string;
  verifiedPhone?: string;
}

interface SignupPayload {
  email?: string;
  phone?: string;
  firstName: string;
  lastName: string;
  orderId?: string;
  siteId?: string;
  signupWithEmail?: boolean;
  signupWithPhone?: boolean;
  birthday?: string;
  joinLoyalty: boolean | null;
  termsOfUse: boolean;
}

interface SignupResponse {
  customer: AuthenticatedUser;
  tokens: Tokens;
}

interface UpdateCustomerPayload {
  firstName?: string;
  lastName?: string;
  email?: string;
  loyaltyCheckbox?: boolean | null;
  allowEmail?: boolean | null;
}

interface RefreshPayload {
  refresh_token: string;
}

interface PurchaseAssetPayload {
  assetId: string;
}

interface PurchaseAssetResponse {
  assetKey: string;
}

export type AuthHeaders = Record<string, string>;

export const makeAuthHeaders = (
  request: Request,
  token?: string,
): AuthHeaders => {
  const res: Record<string, string> = {};
  if (token) {
    res["Authorization"] = `Bearer ${token}`;
  }
  const ip =
    getClientIPAddress(request.headers) ?? process.env.CLIENT_IP ?? "127.0.0.1"; // Fallback for localhost if IP cannot be determined

  if (ip) {
    res["auth-forwarded-for"] = ip;
  }

  const reqUserAgent = request.headers.get("User-Agent");
  if (reqUserAgent) {
    res["X-Forwarded-User-Agent"] = reqUserAgent;
  }

  return res;
};

export const AuthApi = {
  requestPasswordless: async (request: Request, payload: PasswordlessPayload) =>
    await Client.post<PasswordlessResponse>(
      `org/${getSubdomainFromRequest(request)}/auth/requestPasswordless`,
      {
        ...payload,
      },
      {
        headers: makeAuthHeaders(request, undefined),
      },
    ),
  verify: async (request: Request, payload: VerifyPayload) =>
    await Client.post<VerifyResponse>(
      `org/${getSubdomainFromRequest(request)}/auth/verify`,
      {
        ...payload,
      },
      {
        headers: makeAuthHeaders(request, undefined),
      },
    ),
  verifyTempToken: async (request: Request, payload: VerifyTempPayload) =>
    await Client.post<VerifyResponse>(
      `org/${getSubdomainFromRequest(request)}/auth/verifyTempToken`,
      {
        ...payload,
      },
      {
        headers: makeAuthHeaders(request, undefined),
      },
    ),
  signup: async (
    request: Request,
    authHeaders: AuthHeaders,
    payload: SignupPayload,
  ) =>
    await Client.post<SignupResponse>(
      `org/${getSubdomainFromRequest(request)}/auth/signup`,
      {
        ...payload,
      },
      {
        headers: authHeaders,
      },
    ),
  getCustomer: async (
    customerId: string,
    request: Request,
    authHeaders: AuthHeaders,
  ) =>
    await Client.get<AuthenticatedUser>(
      `org/${getSubdomainFromRequest(request)}/auth/customer/${customerId}`,
      {
        headers: authHeaders,
      },
    ),
  getCustomerAssets: async (
    customerId: string,
    request: Request,
    authHeaders: AuthHeaders,
  ) =>
    await Client.get<AssetInfo>(
      `org/${getSubdomainFromRequest(
        request,
      )}/auth/customer/${customerId}/assets`,
      {
        headers: authHeaders,
      },
    ),
  updateCustomer: async (
    request: Request,
    authHeaders: AuthHeaders,
    customerId: string,
    payload: UpdateCustomerPayload,
  ) =>
    await Client.put(
      `org/${getSubdomainFromRequest(request)}/auth/customer/${customerId}`,
      {
        ...payload,
      },
      {
        headers: authHeaders,
      },
    ),
  deleteSavedCard: async (
    request: Request,
    authHeaders: AuthHeaders,
    customerId: string,
    cardId: string,
  ) =>
    await Client.post(
      `org/${getSubdomainFromRequest(
        request,
      )}/auth/customer/${customerId}/cards/${cardId}/delete`,
      {},
      {
        headers: authHeaders,
      },
    ),
  refreshToken: async (
    request: Request,
    authHeaders: AuthHeaders,
    payload: RefreshPayload,
  ) =>
    await Client.post<Tokens>(
      `org/${getSubdomainFromRequest(request)}/auth/refreshToken`,
      payload,
      {
        headers: authHeaders,
      },
    ),
  purchaseAsset: async (
    request: Request,
    authHeaders: AuthHeaders,
    customerId: string,
    payload: PurchaseAssetPayload,
  ) =>
    await Client.post<PurchaseAssetResponse>(
      `org/${getSubdomainFromRequest(
        request,
      )}/auth/customer/${customerId}/purchaseAsset`,
      {
        ...payload,
      },
      {
        headers: authHeaders,
      },
    ),
};
